import { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  CurrencyDetailPopup,
  NotEnoughTokenStaminaPopup,
} from '../components/Popups';
import { WebPlanDetails } from '../constants/allowance';
import { CurrencyType, StaminaType } from '../constants/currencies';
import { usePopup } from '../context';
import { BalanceType } from '../models/Base';
import { Account, Allowance, Profile } from '../models/UserStore';
import { useUserStore } from '../stores/UserStore';

const { getCurrentUser, fetchAllowance } = useUserStore.getState();

export type CheckBalance = (
  currencyType: CurrencyType,
  fee: number,
  onPay: () => void,
  options?: {
    fromType?: CurrencyType;
    onShowPopup?: () => void;
  },
) => void;

const useGameCurrency = (
  account: Account | null | undefined,
  profile: Profile | null | undefined,
  allowance: Allowance | undefined,
  shouldFetchLatest: boolean = true,
) => {
  const navigate = useNavigate();
  const { showPopup, hidePopup } = usePopup();

  const gameStamina = allowance?.allowances.webPlan.quantities.token ?? 0;
  const creationStamina =
    allowance?.allowances.webPlan.quantities.creationToken ?? 0;
  const token = account?.balance?.token ?? 0;
  const joyPoint = account?.balance?.joyPoint ?? 0;
  const breadSlice = account?.balance?.breadSlice ?? 0;
  const activeWebPlanTier = account?.activeWebPlanTier;
  const isTrialUser = profile?.isTrialUser;
  const allowanceRefilledAt = allowance?.allowances.webPlan.expiredAt;

  const fetchCurrency = useCallback(async () => {
    await getCurrentUser({ headers: { 'cache-control': 'no-cache' } });
    await fetchAllowance({ headers: { 'cache-control': 'no-cache' } });
  }, []);

  useEffect(() => {
    if (account?.userId && shouldFetchLatest) {
      fetchCurrency().catch(console.error);
    }
  }, [account?.userId, fetchCurrency, shouldFetchLatest]);

  const getCurrencyData = useCallback(
    (currencyType: CurrencyType) => {
      const canUpgradeTier = activeWebPlanTier === undefined;

      if (currencyType === StaminaType.GameStamina) {
        return {
          currentAmount: gameStamina,
          totalAmount: WebPlanDetails[activeWebPlanTier ?? 'tier0'].token,
          willRefillAt: isTrialUser ? undefined : allowanceRefilledAt,
          navigateTo: isTrialUser
            ? '/register'
            : canUpgradeTier
            ? '/subscription'
            : undefined,
        };
      }
      if (currencyType === StaminaType.CreationStamina) {
        return {
          currentAmount: creationStamina,
          totalAmount:
            WebPlanDetails[activeWebPlanTier ?? 'tier0'].creationToken,
          willRefillAt: isTrialUser ? undefined : allowanceRefilledAt,
          navigateTo: isTrialUser
            ? '/register'
            : canUpgradeTier
            ? '/subscription'
            : undefined,
        };
      }
      if (currencyType === BalanceType.Token) {
        return {
          currentAmount: token,
          totalAmount: undefined,
          willRefillAt: undefined,
          navigateTo: isTrialUser ? '/register' : '/store',
        };
      }
      if (currencyType === BalanceType.JoyPoint) {
        return {
          currentAmount: joyPoint,
          totalAmount: undefined,
          willRefillAt: undefined,
          navigateTo: undefined,
        };
      }
      if (currencyType === BalanceType.BreadSlice) {
        return {
          currentAmount: breadSlice,
          totalAmount: undefined,
          willRefillAt: undefined,
          navigateTo: '/get-more-bread',
        };
      }
      return undefined;
    },
    [
      gameStamina,
      activeWebPlanTier,
      isTrialUser,
      allowanceRefilledAt,
      creationStamina,
      token,
      joyPoint,
      breadSlice,
    ],
  );

  const showCurrencyDetailPopup = useCallback(
    (currencyType: CurrencyType) => {
      const data = getCurrencyData(currencyType);
      if (data) {
        showPopup(
          <CurrencyDetailPopup
            currencyType={currencyType}
            currentAmount={data.currentAmount}
            totalAmount={data.totalAmount}
            willRefillAt={data.willRefillAt}
            isTrialUser={isTrialUser}
            onPrimary={
              data.navigateTo
                ? () => {
                    hidePopup();
                    navigate(data.navigateTo as string);
                  }
                : undefined
            }
            onCancel={hidePopup}
          />,
        );
      }
    },
    [getCurrencyData, hidePopup, isTrialUser, navigate, showPopup],
  );

  const checkBalance = useCallback<CheckBalance>(
    (
      currencyType,
      fee,
      onPay,
      { fromType = BalanceType.Token, onShowPopup } = {},
    ) => {
      const data = getCurrencyData(currencyType);
      if (!data) {
        return;
      }

      const fromData =
        fromType && fromType !== currencyType
          ? getCurrencyData(fromType)
          : undefined;

      if (fee <= data.currentAmount + (fromData?.currentAmount ?? 0)) {
        return onPay();
      }

      onShowPopup?.();
      return showPopup(
        <NotEnoughTokenStaminaPopup
          currencyType={currencyType}
          feeAmount={fee}
          currentAmount={data.currentAmount}
          totalAmount={data.totalAmount}
          willRefillAt={data.willRefillAt}
          isTrialUser={isTrialUser}
          onPrimary={() => {
            hidePopup();
            if (data.navigateTo) {
              navigate(data.navigateTo);
            }
          }}
          onCancel={hidePopup}
        />,
        { currencies: [currencyType, fromType] },
      );
    },
    [getCurrencyData, hidePopup, isTrialUser, navigate, showPopup],
  );

  return {
    gameStamina,
    creationStamina,
    token,
    joyPoint,
    breadSlice,
    fetchCurrency,
    getCurrencyData,
    showCurrencyDetailPopup,
    checkBalance,
  };
};

export default useGameCurrency;
